import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './MikaConfirmModal.scss';

interface NoteInputConfig {
    /** Label shown above the textarea — falls back to a default i18n string. */
    label?: string;
    /** Placeholder for the textarea — falls back to a default i18n string. */
    placeholder?: string;
}

interface MikaConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Called when the user confirms. Receives the trimmed note string if the
     * modal had a note input enabled, otherwise undefined. Existing callers
     * that ignore the argument (signature `() => void`) keep working unchanged.
     */
    onConfirm: (note?: string) => void;
    title: string;
    // Accepts both plain strings (existing usage) and React nodes (so callers can compose
    // rich messages, e.g. a cascade-counts list with inline emphasis on each number).
    message: React.ReactNode;
    confirmLabel?: string;
    confirmColor?: 'danger' | 'primary';
    /**
     * When provided, renders an optional textarea below the message. The note's
     * value is collected internally and passed to `onConfirm`. Useful for capturing
     * the operator's reason on destructive actions (delete cascade audit trail).
     */
    noteInput?: NoteInputConfig;
}

const MikaConfirmModal: React.FC<MikaConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel,
    confirmColor = 'danger',
    noteInput,
}) => {
    const intl = useIntl();
    const [note, setNote] = useState('');

    // Reset the note whenever the modal re-opens — old text from a prior confirm
    // shouldn't bleed into a new prompt for a different record.
    useEffect(() => {
        if (isOpen) setNote('');
    }, [isOpen]);

    if (!isOpen) return null;

    const resolvedConfirmLabel = confirmLabel ?? intl.formatMessage({
        id: 'common.confirm.confirm',
        defaultMessage: 'Confirm',
    });

    const noteLabel = noteInput?.label ?? intl.formatMessage({
        id: 'common.confirm.noteLabel',
        defaultMessage: 'Reason (optional)',
    });
    const notePlaceholder = noteInput?.placeholder ?? intl.formatMessage({
        id: 'common.confirm.notePlaceholder',
        defaultMessage: 'Why is this being deleted? — visible later in Recently Deleted',
    });

    const handleConfirm = () => {
        const trimmed = note.trim();
        // Pass `undefined` when there's no note input OR when the field is empty,
        // so backend treats it as "no note provided" rather than an empty string.
        onConfirm(noteInput ? (trimmed || undefined) : undefined);
    };

    return (
        <div className="mika-confirm-overlay" onClick={onClose}>
            <div className="mika-confirm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mika-confirm-content">
                    <h3 className="mika-confirm-title">{title}</h3>
                    <p className="mika-confirm-message">{message}</p>

                    {noteInput && (
                        <div className="mika-confirm-note">
                            <label className="mika-confirm-note-label">{noteLabel}</label>
                            <textarea
                                className="mika-confirm-note-input"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={notePlaceholder}
                                rows={3}
                                maxLength={500}
                            />
                        </div>
                    )}
                </div>

                <div className="mika-confirm-footer">
                    <button className="mika-confirm-btn mika-btn-cancel" onClick={onClose} type="button">
                        <FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" />
                    </button>
                    <button
                        className={`mika-confirm-btn mika-btn-${confirmColor}`}
                        onClick={handleConfirm}
                        type="button"
                    >
                        {resolvedConfirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MikaConfirmModal;
